"use client"

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import { Button } from '@open-mercato/ui/primitives/button'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'

type DocumentRow = {
  id: string
  participant_profile_id: string
  document_type: string
  document_number: string
  document_name: string | null
  holder_type: string
  issuer_name: string | null
  valid_from: string | null
  valid_until: string | null
  is_indefinite: boolean
  status: string
  verification_status: string
  insured_sum: number | null
  insured_sum_currency: string | null
  territorial_scope: string | null
}

function mapRow(raw: Record<string, unknown>): DocumentRow {
  return {
    id: raw.id as string,
    participant_profile_id: raw.participant_profile_id as string,
    document_type: raw.document_type as string,
    document_number: raw.document_number as string,
    document_name: (raw.document_name as string) ?? null,
    holder_type: raw.holder_type as string,
    issuer_name: (raw.issuer_name as string) ?? null,
    valid_from: (raw.valid_from as string) ?? null,
    valid_until: (raw.valid_until as string) ?? null,
    is_indefinite: (raw.is_indefinite as boolean) ?? false,
    status: raw.status as string,
    verification_status: raw.verification_status as string,
    insured_sum: (raw.insured_sum as number) ?? null,
    insured_sum_currency: (raw.insured_sum_currency as string) ?? null,
    territorial_scope: (raw.territorial_scope as string) ?? null,
  }
}

const statusBadgeClass: Record<string, string> = {
  VALID: 'bg-green-100 text-green-800',
  EXPIRING_SOON: 'bg-yellow-100 text-yellow-800',
  EXPIRED: 'bg-red-100 text-red-800',
  SUSPENDED: 'bg-gray-100 text-gray-800',
  NOT_PROVIDED: 'bg-gray-100 text-gray-600',
  REJECTED: 'bg-red-100 text-red-800',
}

const verificationBadgeClass: Record<string, string> = {
  UNVERIFIED: 'bg-gray-100 text-gray-800',
  PENDING_REVIEW: 'bg-blue-100 text-blue-800',
  VERIFIED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
}

export default function ComplianceDocumentsListPage() {
  const t = useT()
  const router = useRouter()
  const orgVersion = useOrganizationScopeVersion()
  const { confirm } = useConfirmDialog()

  const [data, setData] = React.useState<DocumentRow[]>([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(1)
  const [pageSize] = React.useState(25)
  const [loading, setLoading] = React.useState(true)
  const [filters, setFilters] = React.useState<Record<string, string>>({})

  const fetchData = React.useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      })
      if (filters.documentType) params.set('documentType', filters.documentType)
      if (filters.status) params.set('status', filters.status)
      if (filters.verificationStatus) params.set('verificationStatus', filters.verificationStatus)
      if (filters.holderType) params.set('holderType', filters.holderType)

      const response = await apiCall(`compliance/documents?${params}`)
      const items = ((response as { result?: { items?: Record<string, unknown>[] } })?.result?.items ?? []).map(mapRow)
      setData(items)
      setTotal((response as { result?: { total?: number } })?.result?.total ?? 0)
    } catch {
      flash(t('compliance.errors.loadFailed'), 'error')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, filters, t])

  React.useEffect(() => {
    fetchData()
  }, [fetchData, orgVersion])

  const handleDelete = React.useCallback(async (row: DocumentRow) => {
    const confirmed = await confirm({
      title: t('compliance.actions.deleteConfirm'),
      text: t('compliance.actions.deleteDescription'),
    })
    if (!confirmed) return
    try {
      await apiCallOrThrow('compliance/documents', {
        method: 'DELETE',
        body: JSON.stringify({ id: row.id }),
      })
      flash(t('compliance.actions.deleted'), 'success')
      fetchData()
    } catch {
      flash(t('compliance.errors.deleteFailed'), 'error')
    }
  }, [confirm, fetchData, t])

  const columns = React.useMemo(() => [
    {
      accessorKey: 'document_type',
      header: t('compliance.fields.documentType'),
      cell: ({ row }: { row: { original: DocumentRow } }) => (
        <span className="text-sm font-medium">{t(`compliance.document_type.${row.original.document_type}`)}</span>
      ),
    },
    {
      accessorKey: 'document_number',
      header: t('compliance.fields.documentNumber'),
    },
    {
      accessorKey: 'holder_type',
      header: t('compliance.fields.holderType'),
      cell: ({ row }: { row: { original: DocumentRow } }) => t(`compliance.holder_type.${row.original.holder_type}`),
    },
    {
      accessorKey: 'issuer_name',
      header: t('compliance.fields.issuerName'),
      cell: ({ row }: { row: { original: DocumentRow } }) => row.original.issuer_name ?? '-',
    },
    {
      accessorKey: 'valid_from',
      header: t('compliance.fields.validFrom'),
      cell: ({ row }: { row: { original: DocumentRow } }) =>
        row.original.valid_from ? new Date(row.original.valid_from).toLocaleDateString() : '-',
    },
    {
      accessorKey: 'valid_until',
      header: t('compliance.fields.validUntil'),
      cell: ({ row }: { row: { original: DocumentRow } }) =>
        row.original.is_indefinite
          ? t('compliance.fields.isIndefinite')
          : row.original.valid_until
            ? new Date(row.original.valid_until).toLocaleDateString()
            : '-',
    },
    {
      accessorKey: 'status',
      header: t('compliance.fields.status'),
      cell: ({ row }: { row: { original: DocumentRow } }) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass[row.original.status] ?? 'bg-gray-100 text-gray-800'}`}>
          {t(`compliance.status.${row.original.status}`)}
        </span>
      ),
    },
    {
      accessorKey: 'verification_status',
      header: t('compliance.fields.verificationStatus'),
      cell: ({ row }: { row: { original: DocumentRow } }) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${verificationBadgeClass[row.original.verification_status] ?? 'bg-gray-100 text-gray-800'}`}>
          {t(`compliance.verification_status.${row.original.verification_status}`)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }: { row: { original: DocumentRow } }) => (
        <RowActions
          items={[
            {
              id: 'view',
              label: t('common.view'),
              onSelect: () => router.push(`/backend/compliance-documents/${row.original.id}`),
            },
            {
              id: 'delete',
              label: t('common.delete'),
              destructive: true,
              onSelect: () => handleDelete(row.original),
            },
          ]}
        />
      ),
    },
  ], [t, router, handleDelete])

  const filterDefs = React.useMemo(() => [
    {
      id: 'documentType',
      label: t('compliance.fields.documentType'),
      type: 'select' as const,
      options: [
        { value: 'ROAD_CARRIER_AUTHORIZATION', label: t('compliance.document_type.ROAD_CARRIER_AUTHORIZATION') },
        { value: 'ROAD_LICENSE_DOMESTIC', label: t('compliance.document_type.ROAD_LICENSE_DOMESTIC') },
        { value: 'ROAD_LICENSE_EU_COMMUNITY', label: t('compliance.document_type.ROAD_LICENSE_EU_COMMUNITY') },
        { value: 'RAIL_UTK_LICENSE', label: t('compliance.document_type.RAIL_UTK_LICENSE') },
        { value: 'INSURANCE_OCP_ROAD', label: t('compliance.document_type.INSURANCE_OCP_ROAD') },
        { value: 'INSURANCE_OC_BUSINESS', label: t('compliance.document_type.INSURANCE_OC_BUSINESS') },
        { value: 'ADR_COMPANY_CERTIFICATE', label: t('compliance.document_type.ADR_COMPANY_CERTIFICATE') },
        { value: 'BUSINESS_REGISTRATION', label: t('compliance.document_type.BUSINESS_REGISTRATION') },
      ],
    },
    {
      id: 'status',
      label: t('compliance.fields.status'),
      type: 'select' as const,
      options: [
        { value: 'VALID', label: t('compliance.status.VALID') },
        { value: 'EXPIRING_SOON', label: t('compliance.status.EXPIRING_SOON') },
        { value: 'EXPIRED', label: t('compliance.status.EXPIRED') },
        { value: 'SUSPENDED', label: t('compliance.status.SUSPENDED') },
        { value: 'NOT_PROVIDED', label: t('compliance.status.NOT_PROVIDED') },
        { value: 'REJECTED', label: t('compliance.status.REJECTED') },
      ],
    },
    {
      id: 'verificationStatus',
      label: t('compliance.fields.verificationStatus'),
      type: 'select' as const,
      options: [
        { value: 'UNVERIFIED', label: t('compliance.verification_status.UNVERIFIED') },
        { value: 'PENDING_REVIEW', label: t('compliance.verification_status.PENDING_REVIEW') },
        { value: 'VERIFIED', label: t('compliance.verification_status.VERIFIED') },
        { value: 'REJECTED', label: t('compliance.verification_status.REJECTED') },
      ],
    },
    {
      id: 'holderType',
      label: t('compliance.fields.holderType'),
      type: 'select' as const,
      options: [
        { value: 'COMPANY', label: t('compliance.holder_type.COMPANY') },
        { value: 'PERSON', label: t('compliance.holder_type.PERSON') },
        { value: 'VEHICLE', label: t('compliance.holder_type.VEHICLE') },
      ],
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">{t('compliance.title')}</h1>
          <Button asChild>
            <Link href="/backend/compliance-documents/create">{t('compliance.actions.create')}</Link>
          </Button>
        </div>
        <DataTable
          data={data}
          columns={columns}
          isLoading={loading}
          pagination={{
            page,
            pageSize,
            total,
            totalPages: Math.ceil(total / pageSize),
            onPageChange: setPage,
          }}
          filters={filterDefs}
          filterValues={filters}
          onFiltersApply={setFilters}
        />
      </PageBody>
    </Page>
  )
}
